import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Spinner } from "../../Components/Common";
import { useSites } from "./Hooks/useSites";
import { useProjects } from "../Project/Hooks/useProjects";
import { Building2Icon, FolderIcon } from "lucide-react";
import { getProjectDetails } from "../../Services/projectServices";
import toast from "react-hot-toast";
import { toProperCase } from "../../Utils";
import { TOAST_MESSAGES } from "./Constants";
import { FiTrash2 } from "react-icons/fi";
import { SITE_LIST } from "../../routes";
import {
  GoogleMap,
  Marker,
  Polygon,
  StandaloneSearchBox,
  useJsApiLoader,
} from "@react-google-maps/api";
const containerStyle = { width: "100%", height: "450px" };
const center = { lat: 18.5204, lng: 73.8567 }; // Pune city center
const libraries = ["places"];

const CreateSite = () => {
  const { createSite, loading } = useSites({ fetch: false });
  const { projects, fetchProjects } = useProjects();
  const navigate = useNavigate();
  const searchBoxRef = useRef(null);
  const [map, setMap] = useState(null);
  const [projectData, setProjectData] = useState([]);
  const [polygonData, setPolygonData] = useState(null);
  const [polygonPath, setPolygonPath] = useState([]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const initialValues = {
    project: "",
    site_name: "",
    state: "",
    region: "",
    zone: "",
    district: "",
    location: "",
  };

  const validationSchema = Yup.object({
    project: Yup.string().required("Project must be selected"),
    site_name: Yup.string().required("Site name is required"),
    state: Yup.string().required("State is required"),
    district: Yup.string().required("District is required"),
    region: Yup.string().required("Region is required"),
    zone: Yup.string().required("Zone is required"),
    location: Yup.string().required("Location is required"),
  });

  const handleSubmit = async (data) => {
    const payload = {
      ...data,
      geofence: polygonData,
    };
    await createSite(payload);
    navigate(SITE_LIST);
  };

  const handlePlacesChanged = () => {
    const places = searchBoxRef.current.getPlaces();
    if (places.length > 0) {
      const location = places[0].geometry.location;
      map.panTo({ lat: location.lat(), lng: location.lng() });
      map.setZoom(16);
    }
  };

  const handleProjectChange = async (projectId, setFieldValue) => {
    setFieldValue("project", projectId);
    if (!projectId) {
      setProjectData(null);
      return;
    }

    try {
      const response = await getProjectDetails(projectId);
      if (response.status === 200) {
        setProjectData(response.data);
        setFieldValue("state", response.data.state || "");
        setFieldValue("district", response.data.district || "");
        setFieldValue("region", response.data.region || "");
        setFieldValue("zone", response.data.zone || "");
      } else {
        toast.error(TOAST_MESSAGES.PROJECT_DETAILS_FETCH_ERROR);
      }
    } catch (err) {
      toast.error(TOAST_MESSAGES.PROJECT_DETAILS_FETCH_ERROR);
    }
  };

  useEffect(() => {
    if (polygonPath.length > 2) {
      const geojson = {
        type: "Polygon",
        coordinates: [[...polygonPath.map(p => [p.lng, p.lat]), [polygonPath[0].lng, polygonPath[0].lat]]],
      };
      setPolygonData(JSON.stringify(geojson));
    }
  }, [polygonPath]);

  return (
    <div className="mx-2 my-1  max-h-[calc(100vh-100px)] overflow-y-auto">
      {/* Header */}
      <div className="flex flex-row items-center justify-between gap-4 mx-2 pt-3 md:pt-0 m-2 md:m-5">
        <div className="flex justify-start gap-4 items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-primary text-center text-sm font-lg"
          >
            <IoMdArrowRoundBack size={20} className="text-primary" />
          </button>
          <h1 className="text-xl md:text-2xl text-center text-secondary font-semibold">
            Create New Site
          </h1>
        </div>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={false}
      >
        {({ setFieldValue }) => (
          <Form className="w-full grid grid-cols-1 md:grid-cols-2 mx-auto gap-4 md:gap-0 p-4 md:p-0">
            {/* Project Section */}
            <div className="space-y-3 bg-white rounded-2xl md:ml-12 md:pt-0">
              <div className="pr-0 md:pr-8 border-gray-200">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <FolderIcon className="w-6 h-6 text-secondary" />
                  <h2 className="text-xl font-semibold text-secondary">
                    Project Details
                  </h2>
                </div>
                <div className="space-y-3">
                  {/* Project Dropdown */}
                  <div className="grid grid-cols-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Project <sub className="text-red-500 top-0">*</sub>
                    </label>
                    <Field
                      as="select"
                      name="project"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:border-blue-500 focus:ring-blue-200 focus:outline-none max-h-40 overflow-y-auto"
                      disabled={loading}
                      onChange={(e) =>
                        handleProjectChange(e.target.value, setFieldValue)
                      }
                    >
                      <option value="">Select project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="project"
                      component="div"
                      className="text-red-500 text-xs mt-1 pl-1"
                    />
                  </div>

                  {/* Display Project Details */}

                  <>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700">
                          Type
                        </label>
                        <div className="px-3 py-2 text-sm">
                          {projectData?.type ? (
                            projectData.type === "EV" ? (
                              "EV"
                            ) : ["SOLAR", "BOTH", "OTHER"].includes(
                              projectData.type
                            ) ? (
                              toProperCase(projectData.type)
                            ) : (
                              <span className="italic text-gray-700">
                                Select project
                              </span>
                            )
                          ) : (
                            <span className="italic text-gray-700">
                              Select project
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700">
                          Category
                        </label>
                        <div className="px-3 py-2 text-sm">
                          {projectData.category ? (
                            toProperCase(projectData.category)
                          ) : (
                            <span className="italic text-gray-700">
                              Select project
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700">
                          Client Name
                        </label>
                        <div className="px-3 py-2 text-sm">
                          {projectData.client_name || (
                            <span className="italic text-gray-700">
                              Select project
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700">
                          Project Status
                        </label>
                        <div className="px-3 py-2 text-sm">
                          {projectData.status ? (
                            toProperCase(projectData.status)
                          ) : (
                            <span className="italic text-gray-700">
                              Select project
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                </div>
              </div>
            </div>

            {/* Site Section */}
            <div className="space-y-6 md:mr-4 pr-0  mt-4 md:mt-0">
              <div className="pr-0 md:pr-8 border-gray-200  bg-white md:px-8 md:pt-0 lg:border-l-2">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <Building2Icon className="w-6 h-6 text-secondary" />
                  <h2 className="text-xl font-semibold text-secondary">
                    Site Details
                  </h2>
                </div>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                  {/* Site Name */}
                  <div className="grid grid-cols-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Site Name <sub className="text-red-500 top-0">*</sub>
                    </label>
                    <Field
                      name="site_name"
                      type="text"
                      placeholder="Enter site name"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                      disabled={loading}
                    />
                    <ErrorMessage
                      name="site_name"
                      component="div"
                      className="text-red-500 text-xs mt-1 pl-1"
                    />
                  </div>

                  {/* State, District, Region, Zone */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        State <sub className="text-red-500 top-0">*</sub>
                      </label>
                      <Field
                        name="state"
                        type="text"
                        placeholder="Enter state"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                      />
                      <ErrorMessage
                        name="state"
                        component="div"
                        className="text-red-500 text-xs pl-1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        District <sub className="text-red-500 top-0">*</sub>
                      </label>
                      <Field
                        name="district"
                        type="text"
                        placeholder="Enter district"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                      />
                      <ErrorMessage
                        name="district"
                        component="div"
                        className="text-red-500 text-xs pl-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Region <sub className="text-red-500 top-0">*</sub>
                      </label>
                      <Field
                        name="region"
                        type="text"
                        placeholder="Enter region"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                      />
                      <ErrorMessage
                        name="region"
                        component="div"
                        className="text-red-500 text-xs mt-1 pl-1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Zone <sub className="text-red-500 top-0">*</sub>
                      </label>
                      <Field
                        name="zone"
                        type="text"
                        placeholder="Enter zone"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                      />
                      <ErrorMessage
                        name="zone"
                        component="div"
                        className="text-red-500 text-xs mt-1 pl-1"
                      />
                    </div>
                  </div>

                  {/* Location */}
                  <div className="grid grid-cols-1 gap-1">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Location <sub className="text-red-500 top-0">*</sub>
                      </label>
                      <Field
                        name="location"
                        type="url"
                        placeholder="Enter Google Maps link here"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                      />
                      <ErrorMessage
                        name="location"
                        component="div"
                        className="text-red-500 text-xs mt-1 pl-1"
                      />
                    </div>
                  </div>
                  {isLoaded ? (
                    <div>
                      <div className="space-y-1 mt-4">
                        <label className="block font-medium text-gray-700">
                          Search Location
                        </label>
                        <StandaloneSearchBox
                          onLoad={(ref) => (searchBoxRef.current = ref)}
                          onPlacesChanged={handlePlacesChanged}
                        >
                          <input
                            type="text"
                            placeholder="Search address in India..."
                            className="w-full px-4 py-2 rounded border-gray-300"
                          />
                        </StandaloneSearchBox>
                      </div>
                      <div className="space-y-1 my-4">
                        <label className="block font-medium text-gray-700">
                          Geofence
                        </label>
                        <div className="relative border rounded shadow overflow-hidden">
                          <GoogleMap
                            mapContainerStyle={containerStyle}
                            center={polygonPath[0] || center}
                            zoom={polygonPath.length ? 16 : 12}
                            onLoad={(mapInstance) => setMap(mapInstance)}
                            options={{
                              scrollwheel: false,
                              draggableCursor: "crosshair",
                            }}
                            onClick={(e) => {
                              const lat = e.latLng.lat();
                              const lng = e.latLng.lng();
                              setPolygonPath((prev) => [...prev, { lat, lng }]);
                            }}
                          >
                            {polygonPath.length > 0 && (
                              <Polygon
                                path={polygonPath}
                                options={{
                                  fillColor: "#2196f3",
                                  fillOpacity: 0.3,
                                  strokeColor: "#2196f3",
                                  strokeWeight: 2,
                                  editable: false, // no dragging
                                }}
                              />
                            )}

                            {polygonPath.map((point, index) => (
                              <Marker
                                key={index}
                                position={point}
                                draggable={false}
                                icon={{
                                  path: "M 0,0 m -2,0 a 2,2 0 1,0 4,0 a 2,2 0 1,0 -4,0", // circle
                                  fillColor: "#ff0000",
                                  fillOpacity: 1,
                                  strokeColor: "#ffffff",
                                  strokeWeight: 1,
                                  scale: 2,
                                }}
                              />
                            ))}
                          </GoogleMap>
                          <button
                            type="button"
                            onClick={() => {
                              setPolygonPath([]);
                              setPolygonData(null);
                            }}
                            className="absolute flex items-center gap-1 top-3 left-1/2 -translate-x-1/2 bg-white hover:bg-gray-200 text-gray-600 px-3 py-2 rounded border-2 border-slate-600 z-10 text-lg"
                          >
                            <FiTrash2 />
                            <span className="text-xs font-semibold">
                              Clear Polygon
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-center items-center h-96">
                      <Spinner message="Loading map..." />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end mt-8 md:mr-8">
                <button
                  type="submit"
                  className="px-5 py-2 bg-primary text-white flex justify-center shadow hover:shadow-lg hover:bg-secondary active:scale-95 transition-all text-sm font-medium"
                  disabled={loading}
                >
                  {loading ? <Spinner /> : "Save Site"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateSite;
